import Delta from 'quill-delta';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router';
import * as _ from 'underscore';

import { useErrorBoundary, useFeature, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { FeedbackExternal } from '@treams/treams-network';
import { Popover } from '@treams/treams-ui';

import type { TranscriptionFollowupFeedbackRequestSuggestionData } from '../../../../../Recording/TranscriptionModule';
import { Recipient } from '../../../../kakapo-ui-services/models/Recipient';
import FeedbackRequestModalLoading from './FeedbackRequestModalLoading';
import FeedbackRequestModalMethod from './FeedbackRequestModalMethod';
import FeedbackRequestModalQuestions from './FeedbackRequestModalQuestions';
import FeedbackRequestModalRecipients from './FeedbackRequestModalRecipients';
import FeedbackRequestThankYou from './FeedbackRequestThankYou';

type FeedbackRequestMethod = 'custom' | 'template' | 'competencies' | 'goals' | undefined;

function methodFromSettings(
	momentSettings: graphql.MomentFeedback | undefined,
): FeedbackRequestMethod {
	if (!momentSettings) {
		return undefined;
	}

	if (momentSettings.feedbackTemplate) {
		if (momentSettings.feedbackTemplate.roles?.includes(graphql.FeedbackTemplateRole.GrowthPath)) {
			return 'competencies';
		}

		return 'template';
	}

	return undefined;
}

function stepFromSettings(momentSettings: graphql.MomentFeedback | undefined): number {
	const method = methodFromSettings(momentSettings);

	if (method !== undefined) {
		return 1;
	}

	return 0;
}

function FeedbackRequestModalContents({
	method: defaultMethod,
	userID,
	returnURL,
	refresh,
	momentID,
	momentSettings,
	defaultTitle,
	defaultInstruction,
	defaultTemplate,
}: {
	method?: FeedbackRequestMethod;
	userID: string;
	returnURL: string;
	refresh: () => void;
	momentID?: string;
	momentSettings?: graphql.MomentFeedback;
	defaultTitle?: string;
	defaultInstruction?: Delta;
	defaultTemplate?: graphql.FeedbackTemplate;
}) {
	const { t } = useTranslation();
	const { localize, language } = useLocalize();
	const hasFeedbackFixedCycleRelease = useFeature(
		graphql.FeatureName.FeedbackFixedCycleRelease,
	);
	const hasFeedbackLockCycleTemplate = useFeature(
		graphql.FeatureName.FeedbackLockCycleTemplate,
	);

	const [error, attempt] = useErrorBoundary();

	const [step, setStep] = useState(
		defaultMethod !== undefined ? 1 : stepFromSettings(momentSettings),
	);
	const [method, setMethod] = useState<FeedbackRequestMethod>(
		defaultMethod ?? methodFromSettings(momentSettings),
	);
	const [title, setTitle] = useState<string | undefined>(defaultTitle);
	const [template, setTemplate] = useState<graphql.FeedbackTemplate | undefined>(
		defaultTemplate ?? momentSettings?.feedbackTemplate ?? undefined,
	);
	const [instruction, setInstruction] = useState<Delta | undefined>(
		defaultInstruction ??
			(template?.instruction ? localize(template.instruction)?.text : undefined),
	);
	const [questions, setQuestions] = useState<
		(graphql.FeedbackRequestQuestionInput & { id: string })[]
	>(
		template
			? template.questions?.map(({ question, canSubjectView }) => ({
					id: Math.random().toString(),
					type: question.type,
					text: localize(question.text).text,
					localizedText: question.text,
					subTitle: localize(question.subTitle ?? [])?.text,
					additionalDetails: localize(question.additionalDetails ?? [])?.text,
					canSubjectView,
					options: (question.options ?? []).map((option) => ({
						id: option.id,
						text: option.text,
					})),
				}))
			: [],
	);

	const [goals, setGoals] = useState<{ goalID: string; text?: string; order: number }[]>([]);
	const [competencies, setCompetencies] = useState<{ competencyID: string; text?: string }[]>([]);
	const [recipients, setRecipients] = useState<Recipient[]>([]);
	const [sent, setSent] = useState(false);

	const [create, { loading: loadingCreate }] = graphql.useCreateFeedbackRequestMutation();

	const [requestInternal, { loading: loadingInternal }] =
		graphql.useRequestInternalFeedbackMutation();

	const [requestExternal, { loading: loadingExternal }] =
		graphql.useRequestExternalFeedbackMutation();

	const onChange = useCallback(
		(questions, goals, competencies, title, instruction, template) => {
			setQuestions(questions);
			setGoals(goals);
			setCompetencies(competencies);
			setTitle(title);
			setInstruction(instruction);
			setTemplate(template);
		},
		[setQuestions, setGoals, setCompetencies, setTitle, setInstruction, setTemplate],
	);

	const onDone = attempt(
		async (dueDate: Date, internals: string[], externals: FeedbackExternal[]) => {
			const filledQuestions = questions.filter((question) => question.text !== '');

			const response = await create({
				variables: {
					input: {
						userID,
						questions: filledQuestions
							.map((question) => {
								// `_.omit` widens to `Partial`, but `description` is always
								// provided above, so the result is a valid input question.
								return _.omit(
									{
										...question,
										subTitle: question.subTitle ?? '',
									},
									'id',
									// Drafts can retain options after switching away from multiple
									// choice; only send them for multiple-choice questions.
									...(question.type === graphql.FeedbackQuestionType.MultipleChoice
										? []
										: ['options']),
								) as graphql.FeedbackRequestQuestionInput;
							})
							.concat(
								goals.map(({ goalID, text }) => {
									return {
										text: text ?? t('feedback.composer.goals.progressQuestion'),
										localizedText: [
											{
												languageID: language.id,
												text: text ?? t('feedback.composer.goals.progressQuestion'),
											},
										],
										subTitle: '',
										type: graphql.FeedbackQuestionType.Text,
										goalID,
									};
								}),
							)
							.concat(
								method === 'competencies'
									? competencies.map(({ competencyID, text }) => {
											return {
												text: text ?? t('feedback.composer.competencies.question'),
												localizedText: [
													{
														languageID: language.id,
														text: text ?? t('feedback.composer.competencies.question'),
													},
												],
												subTitle: '',
												type: graphql.FeedbackQuestionType.Text,
												competencyID,
											};
										})
									: [],
							),
						dueDate,
						...(hasFeedbackFixedCycleRelease && momentSettings?.releaseDate
							? { releaseDate: momentSettings.releaseDate }
							: {}),
						title: title || (template ? localize(template.title).text : ''),
						instruction: instruction ?? localize(template?.instruction ?? [])?.text,
						momentID,
						feedbackTemplateID: template?.id,
					},
				},
			});

			const requestID = response.data!.createFeedbackRequest.feedbackRequest.id;

			for (const userID of internals) {
				await requestInternal({ variables: { input: { requestID, userID } } });
			}

			for (const { email, firstName, lastName } of externals) {
				await requestExternal({
					variables: {
						input: {
							requestID,
							email,
							firstName,
							lastName,
						},
					},
				});
			}

			setSent(true);

			refresh();
		},
	);

	const loading = loadingCreate || loadingInternal || loadingExternal;

	if (loading) {
		return <FeedbackRequestModalLoading returnURL={returnURL} />;
	}

	if (sent) {
		return <FeedbackRequestThankYou numberOfRecipients={recipients.length} returnURL={returnURL} />;
	}

	if (step === 0) {
		return (
			<FeedbackRequestModalMethod
				returnURL={returnURL}
				onChange={(method) => {
					setMethod(method);
					setStep(step + 1);

					if (method === 'custom') {
						setQuestions([
							{
								id: Math.random().toString(),
								subTitle: '',
								text: '',
								localizedText: [{ languageID: language.id, text: '' }],
								type: graphql.FeedbackQuestionType.Text,
							},
						]);
					}
				}}
			/>
		);
	}

	if (step === 1) {
		const getTemplateRequirement = () => {
			if (method === 'template') return 'required';
			if (method === 'competencies') return 'optional';
			return undefined;
		};

		return (
			<FeedbackRequestModalQuestions
				method={method ?? 'custom'}
				userID={userID}
				needsTemplate={getTemplateRequirement()}
				instruction={instruction}
				template={template}
				title={title}
				questions={questions}
				goals={goals}
				competencies={competencies}
				returnURL={returnURL}
				onChange={onChange}
				onBack={() => {
					setQuestions([]);
					setInstruction(undefined);
					setTemplate(undefined);
					setStep(step - 1);
				}}
				minimumQuestions={momentSettings?.minimumQuestions ?? undefined}
				templateLocked={
					hasFeedbackLockCycleTemplate && !!momentSettings?.feedbackTemplate
				}
				onDone={() => setStep(step + 1)}
			/>
		);
	}

	if (step === 2) {
		return (
			<FeedbackRequestModalRecipients
				recipients={recipients}
				returnURL={returnURL}
				error={error}
				onBack={() => setStep(step - 1)}
				minimumRecipients={momentSettings?.minimumRecipients ?? undefined}
				maximumRecipients={momentSettings?.maximumRecipients ?? undefined}
				releaseDate={
					hasFeedbackFixedCycleRelease
						? (momentSettings?.releaseDate ?? undefined)
						: undefined
				}
				feedbackTemplateID={template?.id}
				onChange={setRecipients}
				onDone={onDone}
			/>
		);
	}

	return null;
}

export default function FeedbackRequestModal({
	method,
	userID,
	refresh,
	returnURL,
}: {
	method?: FeedbackRequestMethod;
	userID: string;
	returnURL: string;
	refresh: () => void;
}) {
	return (
		<Popover visible center>
			<FeedbackRequestModalContents
				method={method}
				userID={userID}
				returnURL={returnURL}
				refresh={refresh}
			/>
		</Popover>
	);
}

export function FeedbackRequestModalMoment({
	method,
	userID,
	refresh,
	returnURL,
}: {
	method?: FeedbackRequestMethod;
	userID: string;
	returnURL: string;
	refresh: () => void;
}) {
	const { localize } = useLocalize();
	const { params } = useRouteMatch<{ momentID: string }>();

	const { data: momentInstanceData } = graphql.useMomentForFeedbackQuery({
		variables: { id: params.momentID },
	});

	return (
		<Popover visible center>
			{momentInstanceData ? (
				<FeedbackRequestModalContents
					method={method}
					userID={userID}
					returnURL={returnURL}
					refresh={refresh}
					momentID={params.momentID}
					defaultTitle={localize(momentInstanceData.momentInstance.momentTemplate.title).text}
					momentSettings={
						momentInstanceData.momentInstance.momentTemplate.feedback as graphql.MomentFeedback
					}
				/>
			) : (
				<FeedbackRequestModalLoading returnURL={returnURL} />
			)}
		</Popover>
	);
}

export function FeedbackRequestModalSuggestion({
	userID,
	refresh,
	returnURL,
}: {
	userID: string;
	refresh: () => void;
	returnURL: string;
}) {
	const history = useHistory();

	if (!history.location.state?.suggestion) {
		// This route should only be accessed through a suggestion, with the appropriate navigation state set.
		history.replace(returnURL);
	}

	const suggestion = history.location.state
		.suggestion as TranscriptionFollowupFeedbackRequestSuggestionData;

	const method = suggestion.template ? 'template' : 'custom';

	return (
		<Popover visible center>
			<FeedbackRequestModalContents
				method={method}
				userID={userID}
				returnURL={returnURL}
				refresh={refresh}
				defaultTitle={suggestion.title}
				defaultInstruction={suggestion.instruction ?? undefined}
				defaultTemplate={
					suggestion.template ? (suggestion.template as graphql.FeedbackTemplate) : undefined
				}
			/>
		</Popover>
	);
}
